import { type ForwardedRef, type MouseEvent, useImperativeHandle } from 'react'
import type { PointerEvent as ReactPointerEvent } from 'react'
import type { ResizeHandle } from '../types/interaction-state'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { ShapeTextWrapContextApi } from './use-shape-text-wrap-context'
import type { ShapeBoundsApi } from './use-shape-bounds'
import type { ShapeCollectionApi } from './use-shape-collection'
import type { ShapeLockFreezeApi } from './use-shape-lock-freeze'
import type { ShapeUpdatesApi } from './use-shape-updates'
import type { SelectionPresentationApi } from './use-selection-presentation'
import type { FeedbackPulsesApi } from './use-feedback-pulses'
import type { ShapeSelectionApi } from './use-shape-selection'
import type { ShapeLayeringApi } from './use-shape-layering'
import type { ShapeGroupingApi } from './use-shape-grouping'
import type { ShapeUngroupingApi } from './use-shape-ungrouping'
import type { PanelMultiSelectRotationApi } from './use-panel-multi-select-rotation'
import type { MultiSelectChromeCommandsApi } from './use-multi-select-chrome-commands'
import type { ShapeTextCommitApi } from './use-shape-text-commit'
import type { ChainTextSelectionApi } from './use-chain-text-selection'
import type { ChainTextClipboardApi } from './use-chain-text-clipboard'
import type { ShapeTextEditingApi } from './use-shape-text-editing'
import type { InteractionControlApi } from './use-interaction-control'
import type { GestureStartApi } from './use-gesture-start'
import type { ShapeHitTestingApi } from './use-shape-hit-testing'
import type { ShapeClickHandlersApi } from './use-shape-click-handlers'
import type { SelectionBoxPointerHandlersApi } from './use-selection-box-pointer-handlers'
import type { ShapeTextReplaceApi } from './use-shape-text-replace'

export type ShapeManagerHandleContext = ShapeManagerStateApi &
  ShapeTextWrapContextApi &
  ShapeBoundsApi &
  ShapeCollectionApi &
  ShapeLockFreezeApi &
  ShapeUpdatesApi &
  SelectionPresentationApi &
  FeedbackPulsesApi &
  ShapeSelectionApi &
  ShapeLayeringApi &
  ShapeGroupingApi &
  ShapeUngroupingApi &
  PanelMultiSelectRotationApi &
  MultiSelectChromeCommandsApi &
  ShapeTextCommitApi &
  ChainTextSelectionApi &
  ChainTextClipboardApi &
  ShapeTextEditingApi &
  InteractionControlApi &
  GestureStartApi &
  ShapeHitTestingApi &
  ShapeClickHandlersApi &
  SelectionBoxPointerHandlersApi &
  ShapeTextReplaceApi

/**
 * Exposes `ShapeManagerRef`. Called last in `ShapeManager`: the handle maps onto functions defined
 * by every other hook, and React builds it during commit, after the whole render has run.
 */
export function useShapeManagerHandle(
  ref: ForwardedRef<ShapeManagerRef>,
  ctx: ShapeManagerHandleContext
): void {
  const {
    addShape,
    addShapeData,
    altHoveredShapeId,
    applyMultiSelectPanelDimensionCommit,
    applyPanelMultiSelectRotationPreview,
    applyShapeHistoryPatches,
    applyShapeListOps,
    applyShapeTextRunStyle,
    beginPanelMultiSelectRotation,
    bringForward,
    bringToFront,
    canBringForward,
    canGroupSelectedObjects,
    canSendBackward,
    canUngroupSelectedObjects,
    getUngroupBlockedMessage,
    cancelActiveResize,
    cancelActiveRotate,
    chainFrameIdsByShapeIdRef,
    clearSelection,
    clearShapes,
    consumePendingRotateChromeBefore,
    deleteSelectedShape,
    editingShapeTextIdRef,
    enterShapeTextEdit,
    execChainClipboard,
    exitShapeTextEdit,
    finishPanelMultiSelectRotation,
    flushPendingShapeTextCommit,
    freezeSelected,
    geometryBlockedPulseKey,
    getLastUsedStyles,
    setLastUsedStyles,
    getLocalPointFromClient,
    getMultiSelectChromeRotationDeg,
    getMultiSelectChromeSnapshot,
    getSelectedShapeIds,
    getSelectedShapeIsTextFrame,
    getSelectedShapeTextParagraphs,
    getShapeBounds,
    getShapes,
    getSnappedNudgeItemsRef,
    getStackAtPoint,
    groupSelectedObjects,
    handleMultiSelectChromeBoxPointerDown,
    handleResizeStart,
    handleRotateStart,
    handleSelectionBoxContextMenu,
    handleSelectionBoxPointerDown,
    handleShapeDoubleClickInternal,
    isFrozenOnlyMultiSelected,
    isInteracting,
    isLockedMultiSelected,
    isMultiSelect,
    isSingleFrozenOnlySelected,
    isSingleLockedSelected,
    loadShapes,
    lockSelected,
    multiSelectChromeRef,
    mutateChainTextSelection,
    onUnfreezeSelectionBadgeClick,
    onUnlockSelectionBadgeClick,
    panelDimensionsLinkedRef,
    recomputePassiveGroupBounds,
    removeShape,
    reorderShapes,
    replaceShapeTextRanges,
    restoreMultiSelectChrome,
    resyncMultiSelectChromeFromShapes,
    resyncShapeTextEditBaseline,
    seedChainTextSelectionOverFrames,
    selectAll,
    selectNone,
    selectShapes,
    selectShapesForHistory,
    selectShapesWithHistory,
    selectedShapeIdsRef,
    selectedShapes,
    sendBackward,
    sendToBack,
    setAltHoveredShapeId,
    setIgnoreTextWrapSelected,
    shapeTextEditorRef,
    shapeTextOverflowIdsRef,
    shapeTextWrapContextRef,
    triggerSelectionChromeBlockedPulse,
    unfreezeAll,
    unfreezeSelected,
    ungroupSelectedObjects,
    unlockAll,
    unlockSelected,
    updatePanelRotationGuidePreview,
    updateShape,
  } = ctx

  useImperativeHandle(ref, () => ({
    addShape,
    addShapeData,
    loadShapes,
    reorderShapes,
    removeShape,
    updateShape,
    applyShapeHistoryPatches,
    applyShapeListOps,
    getShapes,
    getShapeTextWrapContext: () => shapeTextWrapContextRef.current,
    getSnappedNudgeItems: (rawDx: number, rawDy: number) =>
      getSnappedNudgeItemsRef.current(rawDx, rawDy),
    // A frame click isn't always a move: ctrl/shift toggles or cycles the selection target, alt
    // cycles the highlighted shape, and a plain click on empty space inside a multi-select box
    // clears the selection. `handleSelectionBoxPointerDown`/`handleMultiSelectChromeBoxPointerDown`
    // already encode all of that (they predate the chrome migration) — including calling
    // `handleMoveStart` themselves for the plain-click case — so this just picks the one matching
    // the current selection instead of reimplementing the dispatch.
    beginChromeMove: (event: ReactPointerEvent) =>
      isMultiSelect
        ? handleMultiSelectChromeBoxPointerDown(event)
        : handleSelectionBoxPointerDown(event),
    beginChromeResize: (event: ReactPointerEvent, direction: ResizeHandle) =>
      handleResizeStart(event, direction),
    beginChromeRotate: (
      event: ReactPointerEvent,
      rotationCenterClient?: { x: number; y: number }
    ) => handleRotateStart(event, rotationCenterClient),
    getMultiSelectChrome: () => multiSelectChromeRef.current,
    getAltHoveredShapeId: () => altHoveredShapeId,
    getChainFrameIds: () => chainFrameIdsByShapeIdRef.current,
    getChromeSelectionState: () => ({
      badge:
        isSingleLockedSelected || isLockedMultiSelected
          ? 'lock'
          : isSingleFrozenOnlySelected || isFrozenOnlyMultiSelected
            ? 'freeze'
            : null,
      locked: isSingleLockedSelected || isLockedMultiSelected,
      frozenOnly: isSingleFrozenOnlySelected || isFrozenOnlyMultiSelected,
      geometryBlockedPulseKey,
    }),
    handleChromeContextMenu: (event: MouseEvent) => handleSelectionBoxContextMenu(event),
    handleChromeDoubleClick: (event: MouseEvent) => {
      const shape = selectedShapes[0]

      if (shape) handleShapeDoubleClickInternal(shape, event)
    },
    unlockSelectionFromChromeBadge: () => onUnlockSelectionBadgeClick?.(),
    unfreezeSelectionFromChromeBadge: () => onUnfreezeSelectionBadgeClick?.(),
    clearShapes,
    clearSelection,
    selectAll,
    selectNone,
    lockSelected,
    unlockSelected,
    unlockAll,
    freezeSelected,
    unfreezeSelected,
    unfreezeAll,
    setIgnoreTextWrapSelected,
    isInteracting,
    cancelActiveResize,
    cancelActiveRotate,
    getLastUsedStyles,
    setLastUsedStyles,
    deleteSelectedShape,
    selectShapesWithHistory,
    selectShapes,
    selectShapesForHistory,
    getSelectedShapeIds,
    getShapeBounds,
    beginPanelMultiSelectRotation,
    applyPanelMultiSelectRotationPreview,
    finishPanelMultiSelectRotation,
    getMultiSelectChromeRotationDeg,
    updatePanelRotationGuidePreview,
    setPanelDimensionsLinked: (linked: boolean) => {
      panelDimensionsLinkedRef.current = linked
    },
    bringToFront,
    sendToBack,
    bringForward,
    sendBackward,
    canBringForward,
    canSendBackward,
    canGroupSelectedObjects,
    groupSelectedObjects,
    canUngroupSelectedObjects,
    getUngroupBlockedMessage,
    ungroupSelectedObjects,
    recomputePassiveGroupBounds,
    getMultiSelectAlignReferenceShapeId: () => {
      const id = altHoveredShapeId
      const ids = selectedShapeIdsRef.current

      if (id == null || !ids.includes(id)) {
        return null
      }

      return id
    },
    clearAltHoverHighlight: () => {
      if (altHoveredShapeId) {
        setAltHoveredShapeId(null)

        return true
      }

      return false
    },
    setAltHoveredShapeId,
    resyncMultiSelectChromeFromShapes,
    getMultiSelectChromeSnapshot,
    restoreMultiSelectChrome,
    consumePendingRotateChromeBefore,
    applyMultiSelectPanelDimensionCommit,
    enterShapeTextEdit,
    flushPendingShapeTextCommit,
    resyncShapeTextEditBaseline,
    exitShapeTextEdit,
    getEditingShapeTextId: () => editingShapeTextIdRef.current,
    getTextOverflowShapeIds: () => new Set(shapeTextOverflowIdsRef.current),
    applyShapeTextRunStyle,
    getSelectedShapeTextParagraphs,
    getSelectedShapeIsTextFrame,
    getEditingShapeTextSelectionStyle: () => {
      if (!editingShapeTextIdRef.current) return null
      return shapeTextEditorRef.current?.getEditingStyle() ?? null
    },
    applyShapeTextKerningAtCaret: (value: number) => {
      if (!editingShapeTextIdRef.current) return false
      return shapeTextEditorRef.current?.applyKerningAtCaret(value) ?? false
    },
    getEditingShapeTextKerningState: () => {
      if (!editingShapeTextIdRef.current) return null
      return shapeTextEditorRef.current?.getKerningState() ?? null
    },
    execShapeTextCopy: () => {
      // A cross-frame selection can't be read from one textarea, so it is handled first; the
      // helper returns false when the selection is an ordinary in-frame one (WMPCM-9199).
      if (execChainClipboard('copy')) return
      shapeTextEditorRef.current?.execCopy()
    },
    execShapeTextCut: () => {
      if (execChainClipboard('cut')) return
      shapeTextEditorRef.current?.execCut()
    },
    seedChainTextSelectionOverFrames,
    execShapeTextSelectAllInFrame: () => {
      if (!editingShapeTextIdRef.current) return false
      if (!shapeTextEditorRef.current) return false
      shapeTextEditorRef.current.execSelectAll()

      return true
    },
    execShapeTextDelete: (direction) => {
      if (
        mutateChainTextSelection((handle, part) => handle.replaceRange(part.start, part.end, ''))
      ) {
        return true
      }

      if (!editingShapeTextIdRef.current) return false
      return shapeTextEditorRef.current?.execDelete(direction) ?? false
    },
    execShapeTextPaste: async () => {
      await shapeTextEditorRef.current?.execPaste()
    },
    replaceShapeTextRanges,
    restoreShapeTextEditorFocus: () => {
      if (!editingShapeTextIdRef.current) return
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (editingShapeTextIdRef.current) {
            const active = document.activeElement

            if (active?.tagName === 'INPUT' || active?.tagName === 'TEXTAREA') return
            shapeTextEditorRef.current?.focus()
          }
        })
      })
    },
    hitShapeIdAtClientPoint: (clientX: number, clientY: number) => {
      const point = getLocalPointFromClient(clientX, clientY)

      if (!point) return null
      const top = getStackAtPoint(point)[0]

      return top?.id ?? null
    },
    triggerSelectionGeometryBlockedPulse: triggerSelectionChromeBlockedPulse,
  }))
}
